import { useMemo } from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';

import { c56EA99, cCC38FF, cFA9400, cFF005C } from '#/colors';

import { Loadable } from '#/components/shared/Loadable';
import { EthereumTxLink, StarknetTxLink } from '#/components/shared/TxLink';
import CollectFundsAction from '#/components/withdraw/CollectFundsAction';
import DepositFundsAction from '#/components/common/deposit/DepositFundsAction';
import Skeleton from '#/components/shared/Skeleton';

import { useTransfersView } from '#/features/transfers/transfers-context';
import { TransferView } from '#/features/transfers/transfer-view';

import { TableSpec, createTradeOverviewTable } from './table';
import { Empty, Error } from './atoms';

interface Props {
  tableSpec: TableSpec<string>;
}

export default function Transfers(props: Props) {
  const { tableSpec } = props;
  const [Table, Row] = useMemo(
    () => createTradeOverviewTable(tableSpec),
    [tableSpec],
  );
  const { status, transfers, error } = useTransfersView();

  if (status === 'error') {
    return <Error>{error}</Error>;
  }

  const isEmpty = transfers.length === 0;
  const isLoading = status === 'loading' || status === 'disconnected';

  if (isEmpty) {
    return (
      <Loadable isLoading={isLoading}>
        <Empty>You have no transfers</Empty>
      </Loadable>
    );
  }

  return (
    <Loadable isLoading={isLoading}>
      <Table>
        {transfers.map((transfer) => (
          <Row
            key={transfer.id}
            time={
              <span css="font-size: 11px;">
                <span>{transfer.createdTime}</span>
                <br />
                <span css="opacity: 0.7">{transfer.createdDate}</span>
              </span>
            }
            kind={transfer.kindLabel}
            status={
              <TransferStatus
                status={transfer.status}
                label={transfer.statusLabel}
                layer={transfer.statusLayer}
              />
            }
            amount={transfer.amount}
            txHashL2={
              transfer.isWaitingTxHashL2 ? (
                <TxSkeleton />
              ) : (
                <StarknetTxLink hash={transfer.txHashL2} />
              )
            }
            txHashL1={<EthereumTxLink hash={transfer.txHashL1} />}
            action={<TransferAction transfer={transfer} />}
          />
        ))}
      </Table>
    </Loadable>
  );
}

interface TransferStatusProps {
  readonly status: TransferView['status'];
  readonly label: string;
  readonly layer: string;
}

function TransferStatus({ status, label, layer }: TransferStatusProps) {
  return (
    <span>
      <StatusPill status={status}>{label}</StatusPill>{' '}
      {layer.length > 0 && <StatusPill status={status}>{layer}</StatusPill>}
    </span>
  );
}

type StatusColorsMap = {
  [status in TransferView['status']]: { color: string; bgColor: string };
};

const STATUS_STATUS_MAP: StatusColorsMap = {
  PENDING: { color: cFA9400, bgColor: rgba(cFA9400, 0.2) },
  AVAILABLE: { color: cCC38FF, bgColor: rgba(cCC38FF, 0.2) },
  COMPLETED: { color: c56EA99, bgColor: rgba(c56EA99, 0.2) },
  FAILED: { color: cFF005C, bgColor: rgba(cFF005C, 0.2) },
};

const StatusPill = styled.span<{ status: TransferView['status'] }>`
  background-color: ${({ status }) => STATUS_STATUS_MAP[status].bgColor};
  color: ${({ status }) => STATUS_STATUS_MAP[status].color};
  border-radius: 4px;
  padding: 3px 7px;
  font-size: 10px;
  font-weight: 600;
`;

const TxSkeleton = () => <Skeleton width="86px" height="16px" />;

interface TransferActionProps {
  readonly transfer: TransferView;
}

function TransferAction(props: TransferActionProps) {
  const { transfer } = props;

  switch (transfer.action) {
    case 'withdraw-to-wallet':
      return (
        <CollectFundsAction
          label={transfer.actionLabel}
          amountRaw={transfer.amountRaw}
          amountFormatted={transfer.amount}
          asset={transfer.token}
        />
      );
    case 'deposit-to-paraclear':
      return (
        <DepositFundsAction
          label={transfer.actionLabel}
          amountRaw={transfer.amountRaw}
          asset={transfer.token}
        />
      );
    default:
      return null;
  }
}
